import {ref, watch} from 'vue'
import axios from 'axios'
import {useCompany} from './useCompany'

// Whether the current company has been rolled onto group reporting: the
// `use-group-reporting` company option, NOT the GROUP_REPORTING feature. Those
// diverge during rollout -- most companies' plans include the feature but they
// have not been flipped on -- so report pages must key the scheduled/template
// layer off the option, or they would force group reporting onto companies that
// have not been rolled on.
//
// Mirrors usePermissions: module-level reactive state cached in sessionStorage,
// loaded once per company from the existing options/{name} api-internal route,
// and safe to call from setup().
const OPTION_NAME = 'use-group-reporting'
const STORAGE_KEY = 'group_reporting_enabled'
const STORAGE_COMPANY_KEY = 'group_reporting_company_id'

const stored = sessionStorage.getItem(STORAGE_KEY)
const storedCompanyId = sessionStorage.getItem(STORAGE_COMPANY_KEY)

// null = not yet loaded (unknown); true/false = the resolved option value.
const enabled = ref(stored === null ? null : stored === 'true')
const companyId = ref(storedCompanyId || null)
const loadingPromise = ref(null)

watch(enabled, (value) => {
    if (value === null) {
        sessionStorage.removeItem(STORAGE_KEY)
    } else {
        sessionStorage.setItem(STORAGE_KEY, value ? 'true' : 'false')
    }
})

watch(companyId, (value) => {
    if (value) {
        sessionStorage.setItem(STORAGE_COMPANY_KEY, value)
    }
})

export function useGroupReporting(initialCompanyId = null) {
    const {getCompanyId, buildCompanyApiInternalRoute} = useCompany()

    const loadCompany = (targetCompanyId = null) => {
        if (targetCompanyId === null) {
            targetCompanyId = getCompanyId('sonicu')
        }

        // Switching companies invalidates the cached value.
        if (companyId.value && companyId.value !== targetCompanyId) {
            enabled.value = null
            loadingPromise.value = null
        }

        companyId.value = targetCompanyId

        if (enabled.value !== null && companyId.value === targetCompanyId) {
            return Promise.resolve(enabled.value)
        }

        if (loadingPromise.value) {
            return loadingPromise.value
        }

        const url = buildCompanyApiInternalRoute(`options/${OPTION_NAME}`, targetCompanyId)
        const promise = axios.get(url, {validateStatus: false})
            .then((response) => {
                // OptionsController returns { data: { value } }; a missing option
                // yields null, which for this flag means "not rolled on" -> false.
                const value = response.status === 200
                    ? response.data?.data?.value
                    : null
                enabled.value = value === true || value === 1 || value === '1'
                loadingPromise.value = null
                return enabled.value
            })
            .catch((error) => {
                console.error('Error loading group-reporting option:', error)
                loadingPromise.value = null
                // Fail closed: treat an errored lookup as not-rolled-on so a
                // company is never shown group reporting they may not have.
                enabled.value = false
                return false
            })

        loadingPromise.value = promise
        return promise
    }

    if (initialCompanyId) {
        loadCompany(initialCompanyId)
    } else {
        loadCompany()
    }

    return {
        // Reactive: null while loading, then true/false. Bind this into
        // GroupReportTemplate's `use-group-reporting-option` prop.
        groupReportingEnabled: enabled,
        companyId,
        loadCompany,
    }
}
